import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Collapse from "@mui/material/Collapse";
import Badge from "@mui/material/Badge";
import { BsClockFill, BsExclamationTriangleFill } from "react-icons/bs";

import * as API from "../../../api";
import * as CONST from "../../../Constants";
import { useStyles } from "../../../layout/Styles";
import FormPaper from "../../../layout/FormPaper";
import FieldWrapper from "../../../layout/FieldWrapper";
import DateField from "../../../../forms/DateField";

import PromptButtons from "./PromptButtons";
import Container from "../../../layout/Container";
import DateProviderPrompt from "./DateProviderPrompt";

const propTypes = {
  cpr: PropTypes.object.isRequired,
  handleUpdateStatus: PropTypes.function,
  handleUpdated: PropTypes.function,
  handleFlash: PropTypes.function,
  dispatch: PropTypes.func.isRequired,
};
NotCompletedPrompt.prototype = propTypes;

export default function NotCompletedPrompt(props) {
  const styles = useStyles();
  const [values, setValues] = useState({ date: "", comment: "", cpr_id: props.cpr.id });
  const [selected, setSelected] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  async function submitRequest(req) {
    let res = await API.postCprStatus(req);
    if (res.data.cpr) {
      req.option_value == 9
        ? props.dispatch({ ...res.data.cpr, type: "extension" })
        : props.dispatch({ ...res.data.cpr });
      props.handleFlash({
        success:
          req.option_value == 9
            ? CONST.FLASH.EXTENSION_DUE_DATE
            : req.option_value == 8
            ? CONST.FLASH.REJECTED
            : CONST.FLASH.RESPONDED,
      });
      setLoading(false);
      setSubmitting(false);
      props.handleUpdated();
    } else {
      props.handleFlash({
        error: CONST.FLASH.ERROR,
      });
    }
  }

  function handleSubmit() {
    let request = { cpr_id: values.cpr_id, option_value: selected == "no" ? 8 : 9, notification_id: props.cpr.current_notification_id };
    if (selected == "no") {
      submitRequest(
        values.comment
          ? { ...request, comment: values.comment }
          : request
      );
    } else {
      values.date
        ? submitRequest({
            ...request,
            due_date: values.date,
            extension_requested: true,
          })
        : submitRequest({ ...request, extension_requested: true });
    }
  }

  function handleConfirm() {
    submitRequest({ option_value: 1, cpr_id: values.cpr_id });
  }

  const SubmitButton = ({disabled}) => {
    return(
      <Box my={2}>
        <button
          className="prompt confirm submit-not-completed-button"
          disabled={submitting || loading || disabled}
          onClick={(e) => {
            e.preventDefault();
            setSubmitting(true);
            setLoading(true);
            handleSubmit();
          }}
        >
          {"Submit "}
        </button>
      </Box>
    )
  };

  const OriginalDueDate = () => {
    return (
      <Typography color="#014361" mt={2}>
        {CONST.ORIGINAL_DUE_DATE} <b>{props.cpr.originalDueDate}</b>
      </Typography>
    );
  };

  const ExtensionMaxReached = () => {
    return (
      <>
        <Box display="inline-flex">
          <Badge color="error" sx={{ mr: 3, mt: 2 }}>
            <BsExclamationTriangleFill className="warning" />
          </Badge>
          <Typography fontWeight="600" color="#ff004d" variant="h6">
            {props.cpr.primary_code && CONST.EXTENSION_LIMIT_REACHED_PRIMARY(props.patient.vip_number)}
            {!props.cpr.primary_code && CONST.EXTENSION_LIMIT_REACHED_NONPRIMARY(props.patient.vip_number)}
            <OriginalDueDate />
          </Typography>
        </Box>

        <Box my={2}>
          <button
            disabled={submitting || loading}
            className="prompt confirm"
            onClick={(e) => {
              e.preventDefault();
              setSubmitting(true);
              setLoading(true);
              handleConfirm();
            }}
          >
            {!loading ? "Confirm " : "Loading"}
          </button>
        </Box>
      </>
    );
  };

  const ExtensionPrompt = () => {
    const blockDate = props.cpr.original_due_date
      ? new Date(props.cpr.original_due_date)
      : new Date();
    let block = blockDate.setDate(blockDate.getDate() + 60);

    React.useEffect(() => {
      !show && setTimeout(() => setShow(true), 500);
    });
    var today = new Date();
    var dateSelected = values.date ? new Date(values.date) : null;
    var month = dateSelected ? dateSelected.getMonth() + 1 : null;
    var year = dateSelected ? dateSelected.getFullYear() : null;
    var day = dateSelected ? dateSelected.getDate() : null;
    var disableSubmit = dateSelected && block ? dateSelected.getTime() > block || dateSelected <  today.getTime() : false;
    return (
      <>
        <Typography variant="h5" color="#1a247e" fontWeight="600" mb={1.5}>
          {CONST.EXTENSION_PROMPT}
        </Typography>
        <Collapse in={show}>
          <Fade in={show}>
            <Box display="inline-flex">
              <Badge color="error" sx={{ mr: 3, mt: 2 }} badgeContent={1}>
                <BsClockFill className="clock" />
              </Badge>
              <Typography fontWeight="600" variant="h6" color="#ff004d" mb={2}>
                {CONST.EXTENSION_TEXT} <OriginalDueDate />
              </Typography>
            </Box>
          </Fade>
        </Collapse>
        <DateField
          maxDate={new Date(block)}
          month={month}
          day={day}
          year={year}
          onChange={(date) => {
            if(date) {
              setValues({ ...values, date: date.toString() });
            }
          }}
        />
        <SubmitButton disabled={disableSubmit} />
      </>
    );
  };


  let appointmentPrompt = props.cpr && props.cpr.primary == true ? CONST.APPOINTMENT.prompt : CONST.APPOINTMENT.nonprimary_prompt;

  return (
    <FormPaper>
      {loading && (
        <div className="text-center">
          <i className="fa fa-spinner fa-spin fa-load-page"></i>
          <br />
          Submitting..
        </div>
      )}
      {!loading && (
        <>
          {!selected && (
            <PromptButtons
              promptText={CONST.INTEND_TO_COMPLETE.prompt}
              buttons={{
                yesText: CONST.INTEND_TO_COMPLETE.options.yes,
                noText: CONST.INTEND_TO_COMPLETE.options.no,
                onSelect: (val) => {
                  setSelected(val);
                },
              }}
            />
          )}
          {selected && selected == "yes" ? (
            <>
              {!appointment && (
                <PromptButtons
                  promptText={appointmentPrompt}
                  buttons={{
                    yesText: CONST.APPOINTMENT.options.yes,
                    noText: CONST.APPOINTMENT.options.no,
                    onSelect: (val) => setAppointment(val),
                  }}
                />
              )}
              {appointment && appointment == "yes" && (
                <DateProviderPrompt
                  {...CONST.DATE_PROVIDER_PROMPTS.SCHEDULED}
                  cpr={props.cpr}
                  dispatch={(payload) => {
                    props.dispatch(payload);
                    props.handleUpdated(payload);
                  }}
                  handleUpdated={props.handleUpdated}
                  handleFlash={(m) => props.handleFlash(m)}
                />
              )}
              {appointment &&
                appointment == "no" &&
                (props.cpr.extensionsMaxed ? <ExtensionMaxReached /> : <ExtensionPrompt />)}
            </>
          ) : (
            selected == "no" && (
              <FieldWrapper
                title={CONST.SELECTED_NO_PROMPT_TEXT}
                children={
                  <Container>
                    <textarea
                      placeholder=""
                      onChange={(e) => setValues({ ...values, comment: e.target.value })}
                    />
                      <SubmitButton />
                  </Container>
                }
              />
            )
          )}
        </>
      )}
    </FormPaper>
  );
}
